import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import SearchFilters from '../components/Search/SearchFilters';
import SimilarityWeights, { DEFAULT_WEIGHTS } from '../components/Search/SimilarityWeights';
import SimilarResults from '../components/Search/SimilarResults';
import QueryMetadataHeader from '../components/Search/QueryMetadataHeader';
import ComparisonView from '../components/Search/ComparisonView';
import ElementsSearchModal from '../components/Search/ElementsSearchPanel';
import DocumentKindStep from '../components/Search/DocumentKindStep';
import AnalysisPreview from '../components/History/AnalysisPreview';
import FolderSelector from '../components/Folder/FolderSelector';
import { searchSimilar, getSimilaritySearchResults, checkDuplicate, startHeuristicSearch } from '../services/similarity';
import { getAnalysis, uploadAnalysis, startAnalysis, getAnalysisProgress, saveComparisonCache, getComparisonCache } from '../services/analyses';
import { getAppConfig } from '../services/settings';
import { getEnabledOperations } from '../services/operations';
import { getFolderStatus, uploadFileToMCP, syncFolder, syncFiles, startBatchAnalysis, getBatchJobStatus } from '../services/folder';
import { getOutlookStatus, getOutlookAuthorizeUrl, disconnectOutlook, downloadOutlookAttachment } from '../services/outlook';
import { fetchAccountFlags } from '../services/auth';
import { scanOrderDocument, getOrderScan } from '../services/orders';
import './SimilaritySearchPage.css';

const SHOW_SIMILARITY_SEARCH = false; // NUBBI-26: oculto temporalmente
const POLLING_INTERVAL = 3000;

const drawingPageNumbersOf = (analysis) => {
  const fullJson = analysis?.result?.full_json || analysis?.result || {};
  return fullJson._page_info?.drawing_page_numbers || [1];
};

const b64ToObjectUrl = async (b64) => {
  const blob = await fetch(`data:image/jpeg;base64,${b64}`).then(r => r.blob());
  return URL.createObjectURL(blob);
};

const queryPagesFromResponse = async (data) => {
  if (data.drawing_pages?.length > 0) {
    return Promise.all(data.drawing_pages.map(async (page) => ({
      page_number: page.page_number,
      imageUrl: await b64ToObjectUrl(page.image_b64),
      imageB64: page.image_b64,
    })));
  }
  if (data.drawing_image) {
    return [{ page_number: 1, imageUrl: await b64ToObjectUrl(data.drawing_image), imageB64: data.drawing_image }];
  }
  return [];
};

const queryPagesFromAnalysis = async (analysisHash, pageNumbers) => Promise.all(pageNumbers.map(async (pn) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/v1/analyses/${analysisHash}/image?page=${pn}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
    if (!response.ok) return { page_number: pn, analysisHash };
    return { page_number: pn, imageUrl: URL.createObjectURL(await response.blob()), analysisHash };
  } catch (e) {
    return { page_number: pn, analysisHash };
  }
}));

const mergeTopOverall = (prev, incoming) => {
  const seen = new Set(prev.map(r => r.analysis_hash));
  const merged = [...prev];
  for (const item of incoming) {
    if (!seen.has(item.analysis_hash)) {
      merged.push(item);
      seen.add(item.analysis_hash);
    }
  }
  return merged.sort((a, b) => b.score - a.score);
};

const SimilaritySearchPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const hash = searchParams.get('hash');
  // LAKPED-26: volver a "¿Qué es este documento?" con un documento ya escaneado.
  const documentoReabierto = searchParams.get('documento');

  const [selectedFile, setSelectedFile] = useState(null);
  const [filters, setFilters] = useState({ limit: 10 });
  const [enabledOperations, setEnabledOperations] = useState([]);
  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });
  const [results, setResults] = useState(null);
  const [scores, setScores] = useState({});
  const [breakdowns, setBreakdowns] = useState({});
  const [queryMetadata, setQueryMetadata] = useState(null);
  const [queryAnalysis, setQueryAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [searchStatus, setSearchStatus] = useState('');
  const [error, setError] = useState('');
  const [previewAnalysis, setPreviewAnalysis] = useState(null);
  const [searchHash, setSearchHash] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState(null);
  const [duplicateScores, setDuplicateScores] = useState({});
  const [duplicateTopOverall, setDuplicateTopOverall] = useState([]);
  const [duplicateImageUrl, setDuplicateImageUrl] = useState(null);
  const [queryDrawingPages, setQueryDrawingPages] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [showDuplicateComparison, setShowDuplicateComparison] = useState(false);
  const [showElementsSearch, setShowElementsSearch] = useState(false);
  const [elementsSearchEnabled, setElementsSearchEnabled] = useState(false);

  // LAKPED-16.1: lo primero al subir un documento es decir si es petición de oferta o pedido.
  const [ordersEnabled, setOrdersEnabled] = useState(false);
  const [documentoAClasificar, setDocumentoAClasificar] = useState(null);
  const [escaneo, setEscaneo] = useState(null);
  const [escaneando, setEscaneando] = useState(false);
  const [errorEscaneo, setErrorEscaneo] = useState(null);

  // NUBBI-102: páginas para búsqueda por elementos.
  // Fallbacks: páginas del check → imagen del duplicado → análisis ya hecho (siempre disponible)
  const elementsQueryPages = (() => {
    if (queryDrawingPages && queryDrawingPages.length > 0) return queryDrawingPages;
    if (duplicateImageUrl) return [{ page_number: 1, imageUrl: duplicateImageUrl }];
    if (queryAnalysis?.hash) {
      return drawingPageNumbersOf(queryAnalysis).map(pn => ({ page_number: pn, analysisHash: queryAnalysis.hash }));
    }
    return [];
  })();

  // Imágenes de la consulta: del check si las trae, si no del análisis ya hecho (check por hash no reconvierte el PDF)
  const applyQueryImages = async (data, analysis) => {
    let pages = await queryPagesFromResponse(data);
    if (pages.length === 0 && analysis?.hash) {
      pages = await queryPagesFromAnalysis(analysis.hash, drawingPageNumbersOf(analysis));
    }
    if (pages.length === 0) return;
    setQueryDrawingPages(pages);
    setDuplicateImageUrl(pages[0]?.imageUrl || null);
  };

  const [hasMCP, setHasMCP] = useState(false);
  const [showFolderSelector, setShowFolderSelector] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStatusText, setAnalysisStatusText] = useState('');
  const [fileUploadedToMCP, setFileUploadedToMCP] = useState(false);
  const [mcpFolderPath, setMcpFolderPath] = useState(null);
  const [mcpFileHash, setMcpFileHash] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const pollingInterval = useRef(null);
  const dragCounter = useRef(0);
  const pendingOutlookDrop = useRef(null);
  const handleOutlookDropRef = useRef(null);

  // Staged search state
  const [stageStatus, setStageStatus] = useState({ stage1: 'pending', stage2: 'pending', stage3: 'pending' });
  const [searchedHashes, setSearchedHashes] = useState([]);
  const [isSearchingStage, setIsSearchingStage] = useState(false);
  const [stageCounts, setStageCounts] = useState({});
  const [heuristicByPage, setHeuristicByPage] = useState({});
  const [similarityThreshold, setSimilarityThreshold] = useState(null);

  // NUBBI-102 (anexo): persistir el estado de la comparativa para poder reabrirla tal cual
  useEffect(() => {
    if (duplicateMatches === null || !queryAnalysis?.hash) return undefined;
    const timer = setTimeout(() => {
      const snapshot = {
        matches: duplicateMatches,
        scores: duplicateScores,
        top_overall: duplicateTopOverall,
        searched_hashes: searchedHashes,
        stage_status: stageStatus,
        stage_counts: stageCounts,
        heuristic_by_page: heuristicByPage,
        page_numbers: queryDrawingPages.map(pg => pg.page_number),
      };
      saveComparisonCache(queryAnalysis.hash, snapshot).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateMatches, duplicateScores, duplicateTopOverall, searchedHashes, stageStatus, stageCounts, heuristicByPage, queryAnalysis?.hash]);


  useEffect(() => {
    loadAccountConfig();
    getAppConfig().then(config => setSimilarityThreshold(config.similarity_threshold)).catch(() => {});
    fetchAccountFlags()
      .then(account => {
        setElementsSearchEnabled(Boolean(account.elements_search_enabled));
        setOrdersEnabled(Boolean(account.orders_enabled));
      })
      .catch(() => {
        setElementsSearchEnabled(false);
        setOrdersEnabled(false);
      });
    getFolderStatus()
      .then(status => setHasMCP(status.enabled))
      .catch(() => setHasMCP(false));
    getOutlookStatus()
      .then(status => setOutlookAccounts(status.accounts || []))
      .catch(() => {});
  }, []);

  // Listen for OAuth popup success
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'oauth_success' && event.data?.provider === 'microsoft') {
        getOutlookStatus().then(s => setOutlookAccounts(s.accounts || [])).catch(() => {});
        if (pendingOutlookDrop.current) {
          const data = pendingOutlookDrop.current;
          pendingOutlookDrop.current = null;
          handleOutlookDropRef.current(data, true);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (hash && searchParams.get('compare')) {
      openComparisonForAnalysis(hash);
    } else if (hash) {
      loadAnalysisFile(hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  useEffect(() => {
    if (documentoReabierto) reabrirDocumento(documentoReabierto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentoReabierto]);

  useEffect(() => {
    if (location.state?.mcpFile && location.state?.originalFile) {
      const { mcpFile, originalFile } = location.state;
      setSelectedFile(originalFile);
      setMcpFolderPath(mcpFile.folder_path);
      setMcpFileHash(mcpFile.file_hash);
      setFileUploadedToMCP(true);
    }
  }, [location.state]);

  useEffect(() => {
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    if (searchHash && (searchStatus === 'pending' || searchStatus === 'processing')) {
      pollingInterval.current = setInterval(async () => {
        try {
          const data = await getSimilaritySearchResults(searchHash);
          setSearchStatus(data.status);

          if (data.status === 'completed') {
            setResults(data.analyses);
            setScores(data.scores || {});
            setBreakdowns(data.breakdowns || {});
            setQueryMetadata(data.query_metadata || null);
            setQueryAnalysis(data.query_analysis || null);
            setLoading(false);
            clearInterval(pollingInterval.current);
          } else if (data.status === 'failed') {
            setError(data.error_message || 'Error al procesar la búsqueda');
            setLoading(false);
            clearInterval(pollingInterval.current);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 3000);

      return () => {
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
        }
      };
    }
  }, [searchHash, searchStatus]);

  const loadAccountConfig = async () => {
    try {
      const opsData = await getEnabledOperations();
      const opIds = (opsData.operations || []).map(op => op.id);
      setEnabledOperations(opIds);
    } catch (err) {
      console.error('Error loading account config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  // NUBBI-102 (anexo): reconstruir la comparativa de un análisis existente (?hash=X&compare=1)
  const openComparisonForAnalysis = async (analysisHash) => {
    setCheckingDuplicate(true);
    setAnalyzing(true);
    setError('');
    setDuplicateMatches(null);
    setDuplicateScores({});
    setDuplicateTopOverall([]);
    setDuplicateImageUrl(null);
    setQueryDrawingPages([]);
    setStageStatus({ stage1: 'pending', stage2: 'pending', stage3: 'pending' });
    setSearchedHashes([]);
    setHeuristicByPage({});
    setAnalysisStatusText('Recuperando análisis...');
    try {
      const analysis = await getAnalysis(analysisHash, true);
      setQueryAnalysis(analysis);
      setSelectedFile(new File([''], analysis.filename));

      // Si la comparativa ya se hizo, reabrirla tal cual (sin recalcular)
      try {
        const { cached } = await getComparisonCache(analysisHash);
        if (cached && cached.matches !== undefined) {
          setAnalysisStatusText('Recuperando comparativa guardada...');
          setDuplicateMatches(cached.matches || []);
          setDuplicateScores(cached.scores || {});
          setDuplicateTopOverall(cached.top_overall || []);
          setSearchedHashes(cached.searched_hashes || []);
          setStageStatus(cached.stage_status || { stage1: 'completed', stage2: 'completed', stage3: 'completed' });
          setStageCounts(cached.stage_counts || {});
          setHeuristicByPage(cached.heuristic_by_page || {});
          const pageNumbers = cached.page_numbers?.length ? cached.page_numbers : drawingPageNumbersOf(analysis);
          const pagesWithUrls = await queryPagesFromAnalysis(analysisHash, pageNumbers);
          setQueryDrawingPages(pagesWithUrls);
          setDuplicateImageUrl(pagesWithUrls[0]?.imageUrl || null);
          setShowDuplicateComparison(true);
          return;
        }
      } catch (e) { /* sin caché: recalcular */ }

      setAnalysisStatusText('Buscando piezas similares...');
      const data = await checkDuplicate(null, { excludeHash: analysisHash, reuseFileFromHash: analysisHash });
      setDuplicateMatches(data.matches || []);
      setDuplicateScores(data.scores || {});
      setDuplicateTopOverall(data.top_overall || []);
      setSearchedHashes(data.searched_hashes || []);
      setStageStatus({ stage1: 'completed', stage2: 'completed', stage3: 'completed' });

      await applyQueryImages(data, analysis);

      setShowDuplicateComparison(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al recuperar la comparativa');
    } finally {
      setCheckingDuplicate(false);
      setAnalyzing(false);
      setAnalysisStatusText('');
    }
  };

  const loadAnalysisFile = async (analysisHash) => {
    try {
      const analysis = await getAnalysis(analysisHash, true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/analyses/${analysisHash}/original`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al descargar el archivo original');
      }

      const blob = await response.blob();
      const file = new File([blob], analysis.filename, { type: blob.type });
      setSelectedFile(file);
    } catch (err) {
      setError('Error al cargar el análisis');
    }
  };

  const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  const ACCEPTED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

  const isFileTypeValid = (file) => {
    if (ACCEPTED_TYPES.includes(file.type)) return true;
    const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    return ext && ACCEPTED_EXTENSIONS.includes(ext);
  };

  const continuarAnalisis = (file) => {
    if (hasMCP) {
      setPendingFile(file);
      setShowFolderSelector(true);
    } else {
      setSelectedFile(file);
    }
  };

  // LAKPED-16.1: el documento se lee por detrás mientras el usuario contesta qué es.
  const leerDocumento = async (file) => {
    setEscaneando(true);
    setEscaneo(null);
    setErrorEscaneo(null);
    try {
      setEscaneo(await scanOrderDocument(file));
    } catch (err) {
      setErrorEscaneo('No se ha podido leer el documento. Se puede seguir igualmente analizando el plano.');
    } finally {
      setEscaneando(false);
    }
  };

  const processFile = (file) => {
    setInfoMessage('');
    setFileUploadedToMCP(false);
    setMcpFolderPath(null);
    setMcpFileHash(null);

    if (ordersEnabled) {
      setDocumentoAClasificar(file);
      leerDocumento(file);
      return;
    }

    continuarAnalisis(file);
  };

  // LAKPED-26: la pantalla de entrada se rehace con lo que ya se leyó (sin pasar otra vez por la IA)
  // y con el fichero original, para que la vista previa y "Cambiar documento" funcionen igual.
  const reabrirDocumento = async (analysisHash) => {
    setEscaneando(true);
    setErrorEscaneo(null);
    try {
      const lectura = await getOrderScan(analysisHash);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/analyses/${analysisHash}/original`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const blob = response.ok ? await response.blob() : new Blob([]);
      setDocumentoAClasificar(new File([blob], lectura.filename, { type: blob.type }));
      setEscaneo(lectura);
    } catch (err) {
      setErrorEscaneo('No se ha podido recuperar el documento.');
    } finally {
      setEscaneando(false);
    }
  };

  const limpiarClasificacion = () => {
    setDocumentoAClasificar(null);
    setEscaneo(null);
    setEscaneando(false);
    setErrorEscaneo(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('Files') || types.includes('attachment')) {
      setIsDragOver(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleConnectOutlook = async () => {
    try {
      const { authorize_url } = await getOutlookAuthorizeUrl();
      window.open(authorize_url, 'outlook_oauth', 'width=600,height=700,left=200,top=100');
    } catch (err) {
      setError('Error al iniciar conexión con Outlook');
    }
  };

  const handleDisconnectOutlook = async (email) => {
    try {
      await disconnectOutlook(email);
      setOutlookAccounts(prev => prev.filter(e => e !== email));
    } catch (err) {
      setError('Error al desconectar Outlook');
    }
  };

  const handleOutlookDrop = async (outlookData, skipAuthCheck = false) => {
    const attachment = outlookData.attachmentFiles?.[0];
    if (!attachment) {
      setError('No se encontró adjunto en el drag de Outlook');
      return;
    }

    const userEmail = outlookData.mailboxInfos?.[0]?.mailboxSmtpAddress || '';

    const isConnected = skipAuthCheck || outlookAccounts.includes(userEmail);
    if (!isConnected) {
      pendingOutlookDrop.current = outlookData;
      handleConnectOutlook();
      return;
    }

    setAnalyzing(true);
    setAnalysisStatusText(`Descargando "${attachment.name}" de Outlook...`);
    setError('');
    setInfoMessage('');

    try {
      const file = await downloadOutlookAttachment(
        attachment.attachmentItemId,
        attachment.name,
        userEmail,
      );

      if (!isFileTypeValid(file)) {
        setError('Tipo de archivo no soportado. Usa PDF, JPG o PNG');
        return;
      }

      processFile(file);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Error al descargar adjunto';
      if (err.response?.status === 401) {
        getOutlookStatus().then(s => setOutlookAccounts(s.accounts || [])).catch(() => {});
        setInfoMessage('La conexión con Outlook ha expirado. Conéctalo de nuevo.');
      } else {
        setError(`Error descargando adjunto de Outlook: ${detail}`);
      }
    } finally {
      setAnalyzing(false);
      setAnalysisStatusText('');
    }
  };
  handleOutlookDropRef.current = handleOutlookDrop;

  const triggerEmailAttachmentDownload = (url, html) => {
    const filenameMatch = html?.match(/class="aV3"[^>]*>([^<]+)<\/span>/)
    const filename = filenameMatch ? filenameMatch[1] : 'adjunto';

    const downloadUrl = url.includes('mail.google.com')
      ? url.replace('disp=inline', 'disp=safe')
      : url;

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setError('');
    setInfoMessage(`"${filename}" se está descargando. Cuando termine, arrástralo aquí desde Descargas o usa "Seleccionar archivo"`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragOver(false);

    if (analyzing) return;

    // 1. Normal file drop (desktop, file manager)
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!isFileTypeValid(file)) {
        setError('Tipo de archivo no soportado. Usa PDF, JPG o PNG');
        return;
      }
      setError('');
      processFile(file);
      return;
    }

    // 2. Outlook Web attachment (dataTransfer type "attachment" with JSON)
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('attachment')) {
      try {
        const raw = e.dataTransfer.getData('attachment');
        const outlookData = JSON.parse(raw);
        if (outlookData.attachmentFiles?.length > 0) {
          handleOutlookDrop(outlookData);
          return;
        }
      } catch (err) {
        console.error('Error parsing Outlook attachment data:', err);
      }
    }

    // 3. Gmail/other email (text/uri-list with download URL)
    const url = e.dataTransfer.getData('text/uri-list');
    if (url && url.startsWith('http')) {
      triggerEmailAttachmentDownload(url, e.dataTransfer.getData('text/html'));
      return;
    }

    setError('No se pudo obtener el archivo. Descárgalo primero y arrástralo desde tu escritorio');
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleWeightsChange = (newWeights) => {
    setWeights(newWeights);
  };

  const performSearch = async () => {
    if (!selectedFile) {
      setError('Por favor seleccione un archivo');
      return;
    }

    const totalWeight = Object.values(weights).reduce((sum, val) => sum + (val || 0), 0);
    if (totalWeight !== 100) {
      setError('Los pesos de similitud deben sumar exactamente 100%');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);
    setQueryMetadata(null);
    setQueryAnalysis(null);
    setSearchStatus('');

    try {
      const data = await searchSimilar(selectedFile, filters, weights, enabledOperations);
      setSearchHash(data.hash);
      setSearchStatus(data.status);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar búsqueda');
      setLoading(false);
    }
  };

  const handleCheckDuplicate = async () => {
    if (!selectedFile) return;

    if (fileUploadedToMCP && mcpFileHash && mcpFolderPath) {
      // File already uploaded to MCP, just analyze
      await analyzeUploadedMCPFile();
    } else {
      // No MCP or file not uploaded yet, use direct analysis
      await analyzeAndCheckDuplicates();
    }
  };

  const analyzeAndCheckDuplicates = async () => {
    setCheckingDuplicate(true);
    setAnalyzing(true);
    setError('');
    setDuplicateMatches(null);
    setDuplicateScores({});
    setDuplicateTopOverall([]);
    setDuplicateImageUrl(null);
    setQueryDrawingPages([]);
    setQueryAnalysis(null);
    setStageStatus({ stage1: 'pending', stage2: 'pending', stage3: 'pending' });
    setSearchedHashes([]);
    setHeuristicByPage({});
    setAnalysisStatusText('Subiendo archivo...');

    try {
      // 1. Subir archivo
      const uploadResult = await uploadAnalysis(selectedFile);
      const analysisHash = uploadResult.hash;

      // 2. Iniciar análisis
      setAnalysisStatusText('Iniciando análisis...');
      await startAnalysis(analysisHash);

      // 3. Polling hasta completado
      setAnalysisStatusText('Analizando plano...');
      await pollAnalysisUntilComplete(analysisHash, setAnalysisStatusText);

      // 4. Obtener análisis completo
      setAnalysisStatusText('Obteniendo resultados...');
      const analysis = await getAnalysis(analysisHash);
      setQueryAnalysis(analysis);

      // 5. Buscar duplicados (sin staged search ya que no hay carpeta MCP)
      setAnalysisStatusText('Buscando piezas similares...');
      const data = await checkDuplicate(null, { excludeHash: analysisHash, reuseFileFromHash: analysisHash });
      setDuplicateMatches(data.matches || []);
      setDuplicateScores(data.scores || {});
      setDuplicateTopOverall(data.top_overall || []);
      setSearchedHashes(data.searched_hashes || []);
      setStageStatus({ stage1: 'completed', stage2: 'completed', stage3: 'completed' });

      await applyQueryImages(data, analysis);

      setShowDuplicateComparison(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al verificar duplicado');
    } finally {
      setCheckingDuplicate(false);
      setAnalyzing(false);
      setAnalysisStatusText('');
    }
  };

  const pollAnalysisUntilComplete = (analysisHash, onProgress) => {
    return new Promise((resolve, reject) => {
      const poll = setInterval(async () => {
        try {
          const progress = await getAnalysisProgress(analysisHash);
          if (onProgress) {
            const baseMsg = progress.progress_message || 'Analizando plano...';
            const opInProgress = (progress.operations || []).find((o) => o.status === 'processing');
            const suffix = opInProgress
              ? ` · ${opInProgress.operation_type || opInProgress.operation || ''}`
              : '';
            onProgress(`${baseMsg}${suffix}`.trim());
          }
          if (progress.status === 'completed') {
            clearInterval(poll);
            resolve(progress);
          } else if (progress.status === 'failed') {
            clearInterval(poll);
            reject(new Error('El análisis ha fallado'));
          }
        } catch (err) {
          clearInterval(poll);
          reject(err);
        }
      }, POLLING_INTERVAL);
    });
  };

  const pollBatchUntilComplete = (batchJobHash, onProgress) => {
    return new Promise((resolve, reject) => {
      const poll = setInterval(async () => {
        try {
          const status = await getBatchJobStatus(batchJobHash);
          if (onProgress) {
            const firstItem = (status.items || [])[0];
            if (firstItem?.analysis_hash) {
              try {
                const progress = await getAnalysisProgress(firstItem.analysis_hash);
                const baseMsg = progress.progress_message || 'Analizando plano...';
                const opInProgress = (progress.operations || []).find((o) => o.status === 'processing');
                const suffix = opInProgress
                  ? ` · ${opInProgress.operation_type || opInProgress.operation || ''}`
                  : '';
                onProgress(`${baseMsg}${suffix}`.trim());
              } catch {
                /* el progress puede fallar al inicio mientras se crea el analysis */
              }
            }
          }
          if (status.status === 'completed') {
            clearInterval(poll);
            resolve(status);
          } else if (status.status === 'failed') {
            clearInterval(poll);
            reject(new Error('El análisis batch ha fallado'));
          }
        } catch (err) {
          clearInterval(poll);
          reject(err);
        }
      }, POLLING_INTERVAL);
    });
  };

  const handleFolderSelected = async (folderPath) => {
    setShowFolderSelector(false);

    if (!pendingFile) return;

    setAnalyzing(true);
    setAnalysisStatusText('Subiendo a carpeta MCP...');

    try {
      // 1. Upload file to MCP
      await uploadFileToMCP(pendingFile, folderPath);

      // 2. Register the uploaded file in DB and get its hash directly
      setAnalysisStatusText('Sincronizando archivo...');
      const folder = folderPath.replace(/^\/+/, '');
      const filePath = folder ? `${folder}/${pendingFile.name}` : pendingFile.name;
      const syncResult = await syncFiles([filePath]);
      const fileHash = Object.values(syncResult.file_hashes || {})[0];

      if (!fileHash) {
        throw new Error('No se pudo obtener el hash del archivo sincronizado');
      }

      // 3. Save MCP state and set selected file
      setMcpFolderPath(folderPath);
      setMcpFileHash(fileHash);
      setFileUploadedToMCP(true);
      setSelectedFile(pendingFile);
      setPendingFile(null);
      setAnalysisStatusText('');
    } catch (err) {
      console.error('Error uploading to MCP:', err);
      setError(err.message || 'Error al subir archivo a MCP');
      setPendingFile(null);
    } finally {
      setAnalyzing(false);
      setAnalysisStatusText('');
    }
  };

  const handleCancelFolderSelector = () => {
    setShowFolderSelector(false);
    setPendingFile(null);
  };

  const analyzeUploadedMCPFile = async () => {
    setCheckingDuplicate(true);
    setAnalyzing(true);
    setError('');
    setDuplicateMatches(null);
    setDuplicateScores({});
    setDuplicateTopOverall([]);
    setDuplicateImageUrl(null);
    setQueryDrawingPages([]);
    setQueryAnalysis(null);
    setStageStatus({ stage1: 'pending', stage2: 'pending', stage3: 'pending' });
    setSearchedHashes([]);
    setHeuristicByPage({});
    setAnalysisStatusText('Iniciando análisis...');

    try {
      // 1. Start batch analysis with the already uploaded file
      const batchJob = await startBatchAnalysis([mcpFileHash]);

      // 2. Polling until complete
      setAnalysisStatusText('Analizando plano...');
      const batchResult = await pollBatchUntilComplete(batchJob.hash, setAnalysisStatusText);

      // 3. Get complete analysis from the batch item (no MCP listing — falla en carpetas grandes)
      setAnalysisStatusText('Obteniendo resultados...');
      const completedItem = batchResult?.items?.find(it => it.analysis_hash);

      let excludeHash = null;
      let analysis = null;
      if (completedItem?.analysis_hash) {
        excludeHash = completedItem.analysis_hash;
        analysis = await getAnalysis(completedItem.analysis_hash);
        setQueryAnalysis(analysis);
      }

      // 4. Stage 1: Search for duplicates in destination folder only
      setAnalysisStatusText('Buscando en carpeta destino...');
      const data = await checkDuplicate(excludeHash ? null : selectedFile, {
        excludeHash,
        reuseFileFromHash: excludeHash || undefined,
        stage: 1,
        destinationFolder: mcpFolderPath
      });
      setDuplicateMatches(data.matches || []);
      setDuplicateScores(data.scores || {});
      setDuplicateTopOverall(data.top_overall || []);
      setSearchedHashes(data.searched_hashes || []);
      setStageCounts({ 1: data.total_searched || 0 });
      setStageStatus({ stage1: 'completed', stage2: 'pending', stage3: 'pending' });

      await applyQueryImages(data, analysis);

      // Always open comparison (even with 0 matches to show stage buttons)
      setShowDuplicateComparison(true);
    } catch (err) {
      console.error('Error analyzing MCP file:', err);
      setError(err.response?.data?.detail || err.message || 'Error al analizar archivo');
    } finally {
      setCheckingDuplicate(false);
      setAnalyzing(false);
      setAnalysisStatusText('');
    }
  };

  const handleQuickSearch = async () => {
    if (!selectedFile) return;

    if (!fileUploadedToMCP) {
      await analyzeAndCheckDuplicates();
      return;
    }

    setCheckingDuplicate(true);
    setAnalyzing(true);
    setError('');
    setDuplicateMatches(null);
    setDuplicateScores({});
    setDuplicateTopOverall([]);
    setDuplicateImageUrl(null);
    setQueryDrawingPages([]);
    setQueryAnalysis(null);
    setStageStatus({ stage1: 'pending', stage2: 'pending', stage3: 'pending' });
    setSearchedHashes([]);
    setHeuristicByPage({});
    setAnalysisStatusText('Iniciando análisis...');

    try {
      const batchJob = await startBatchAnalysis([mcpFileHash]);

      setAnalysisStatusText('Analizando plano...');
      const batchResult = await pollBatchUntilComplete(batchJob.hash, setAnalysisStatusText);

      setAnalysisStatusText('Obteniendo resultados...');
      const completedItem = batchResult?.items?.find(it => it.analysis_hash);

      let excludeHash = null;
      let analysis = null;
      if (completedItem?.analysis_hash) {
        excludeHash = completedItem.analysis_hash;
        analysis = await getAnalysis(completedItem.analysis_hash);
        setQueryAnalysis(analysis);
      }

      setAnalysisStatusText('Buscando piezas similares...');
      const data = await checkDuplicate(excludeHash ? null : selectedFile, {
        excludeHash,
        reuseFileFromHash: excludeHash || undefined,
      });
      setDuplicateMatches(data.matches || []);
      setDuplicateScores(data.scores || {});
      setDuplicateTopOverall(data.top_overall || []);
      setSearchedHashes(data.searched_hashes || []);
      setStageStatus({ stage1: 'completed', stage2: 'completed', stage3: 'completed' });

      await applyQueryImages(data, analysis);

      setShowDuplicateComparison(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error en búsqueda rápida');
    } finally {
      setCheckingDuplicate(false);
      setAnalyzing(false);
      setAnalysisStatusText('');
    }
  };

  const handleSearchRecent = async () => {
    if (!selectedFile) return;

    setIsSearchingStage(true);
    setStageStatus(prev => ({ ...prev, stage2: 'searching' }));

    try {
      const excludeHash = queryAnalysis?.hash || null;
      const data = await checkDuplicate(excludeHash ? null : selectedFile, {
        excludeHash,
        reuseFileFromHash: excludeHash || undefined,
        stage: 2,
        destinationFolder: mcpFolderPath,
        alreadySearchedHashes: searchedHashes
      });

      setDuplicateMatches(prev => [...prev, ...(data.matches || [])]);
      setDuplicateScores(prev => ({ ...prev, ...data.scores }));
      setDuplicateTopOverall(prev => mergeTopOverall(prev, data.top_overall || []));
      setSearchedHashes(prev => [...prev, ...(data.searched_hashes || [])]);
      setStageCounts(prev => ({ ...prev, 2: data.total_searched || 0 }));
      setStageStatus(prev => ({ ...prev, stage2: 'completed' }));
    } catch (err) {
      console.error('Error in Stage 2 search:', err);
      setStageStatus(prev => ({ ...prev, stage2: 'pending' }));
    } finally {
      setIsSearchingStage(false);
    }
  };

  const handleSearchHistorical = async (existingAboveCount = 0) => {
    if (!selectedFile) return;

    setIsSearchingStage(true);
    setStageStatus(prev => ({ ...prev, stage3: 'searching' }));

    try {
      const excludeHash = queryAnalysis?.hash || null;

      const existingBelow = similarityThreshold !== null
        ? (duplicateMatches || [])
            .filter(m => (duplicateScores[m.hash] || 0) < similarityThreshold)
            .map(m => ({ hash: m.hash, score: duplicateScores[m.hash] || 0, query_page: m.query_page }))
        : [];

      const data = await checkDuplicate(excludeHash ? null : selectedFile, {
        excludeHash,
        reuseFileFromHash: excludeHash || undefined,
        stage: 3,
        alreadySearchedHashes: searchedHashes,
        existingAboveCount,
        existingBelowThreshold: existingBelow.length > 0 ? existingBelow : undefined,
        onValidating: () => setStageStatus(prev => ({ ...prev, stage3: 'validating' })),
      });

      const vlmRejected = new Set(data.vlm_rejected || []);
      setDuplicateMatches(prev => {
        const cleaned = vlmRejected.size > 0 ? prev.filter(m => !vlmRejected.has(m.hash)) : prev;
        return [...cleaned, ...(data.matches || [])];
      });
      setDuplicateScores(prev => {
        const newScores = { ...prev, ...data.scores };
        vlmRejected.forEach(h => delete newScores[h]);
        return newScores;
      });
      setDuplicateTopOverall(prev => mergeTopOverall(prev, data.top_overall || []));
      setSearchedHashes(prev => [...prev, ...(data.searched_hashes || [])]);
      setStageCounts(prev => ({ ...prev, 3: data.total_searched || 0 }));
      setStageStatus(prev => ({ ...prev, stage3: 'completed' }));
    } catch (err) {
      console.error('Error in Stage 3 search:', err);
      setStageStatus(prev => ({ ...prev, stage3: 'pending' }));
    } finally {
      setIsSearchingStage(false);
    }
  };

  const handleSearchHeuristic = async (pageNumber) => {
    if (!queryAnalysis?.hash) return;

    const pageKey = pageNumber || 'default';
    setHeuristicByPage(prev => ({ ...prev, [pageKey]: { status: 'searching', results: [], scores: {} } }));

    try {
      const matchesHashes = (duplicateMatches || []).map(m => m.hash);
      const matchesHashesSet = new Set(matchesHashes);
      const rejectedShown = duplicateTopOverall
        .filter(r => !matchesHashesSet.has(r.analysis_hash))
        .slice(0, 10);
      const existingHashes = [
        ...matchesHashes,
        ...rejectedShown.map(r => r.analysis_hash),
      ];

      const data = await startHeuristicSearch(queryAnalysis.hash, {
        existingHashes,
        pageNumber: pageNumber || undefined,
        onValidating: () => setHeuristicByPage(prev => ({
          ...prev, [pageKey]: { ...prev[pageKey], status: 'validating' }
        })),
      });

      setHeuristicByPage(prev => ({
        ...prev, [pageKey]: { status: 'completed', results: data.matches || [], scores: data.scores || {} }
      }));
    } catch (err) {
      console.error('Error in heuristic search:', err);
      setHeuristicByPage(prev => ({ ...prev, [pageKey]: { status: 'idle', results: [], scores: {} } }));
    }
  };

  const handlePreview = async (analysis) => {
    try {
      const fullAnalysis = await getAnalysis(analysis.hash);
      setPreviewAnalysis(fullAnalysis);
    } catch (error) {
      setError('Error al cargar el análisis completo');
    }
  };

  if (loadingConfig) {
    return (
      <div className="similarity-search-page">
        <h1>Análisis de plano</h1>
        <p>Cargando configuración...</p>
      </div>
    );
  }

  if (previewAnalysis) {
    return <AnalysisPreview analysis={previewAnalysis} onClose={() => setPreviewAnalysis(null)} />;
  }

  if (showComparison && results) {
    return (
      <ComparisonView
        queryMetadata={queryMetadata}
        queryAnalysis={queryAnalysis}
        queryFilename={selectedFile?.name}
        searchHash={searchHash}
        results={results}
        scores={scores}
        onClose={() => setShowComparison(false)}
      />
    );
  }

  if (showDuplicateComparison) {
    const imageUrl = duplicateImageUrl || (selectedFile ? URL.createObjectURL(selectedFile) : null);
    return (
      <ComparisonView
        queryFilename={selectedFile?.name}
        queryImageUrl={imageUrl}
        queryDrawingPages={queryDrawingPages}
        queryAnalysis={queryAnalysis}
        results={duplicateMatches || []}
        scores={duplicateScores}
        topOverall={duplicateTopOverall}
        onClose={() => setShowDuplicateComparison(false)}
        onSearchRecent={handleSearchRecent}
        onSearchHistorical={handleSearchHistorical}
        onSearchHeuristic={handleSearchHeuristic}
        heuristicByPage={heuristicByPage}
        stageStatus={stageStatus}
        stageCounts={stageCounts}
        isSearching={isSearchingStage}
        elementsSearchEnabled={elementsSearchEnabled}
        elementsQueryPages={elementsQueryPages}
      />
    );
  }

  if (documentoAClasificar) {
    return (
      <DocumentKindStep
        file={documentoAClasificar}
        escaneo={escaneo}
        escaneando={escaneando}
        error={errorEscaneo}
        onOferta={(datos) => {
          // LAKPED-16.2: antes de montar la oferta se mira qué sabemos ya de esa pieza.
          // LAKPED-21: lo leído en la portada (cliente, nº de pedido, pieza y cantidad) se hereda
          // en todos los casos, se use una base o se empiece de cero.
          const query = new URLSearchParams({ analysis: escaneo?.analysis_hash || '' });
          if (datos.cliente) query.set('cliente', datos.cliente);
          if (datos.articulo) query.set('codigo', datos.articulo);
          if (datos.numero_documento) query.set('referencia', datos.numero_documento);
          if (escaneo?.order_data?.cantidad) query.set('cantidad', escaneo.order_data.cantidad);
          // Una factura sola no trae plano: no hay con qué buscar parecidos.
          if (!(escaneo?.drawing_pages || []).length) query.set('plano', '0');
          navigate(`/offers/history?${query.toString()}`);
        }}
        onPedido={(datos) => navigate('/orders/new', {
          state: { escaneo: { ...escaneo, order_data: { ...(escaneo?.order_data || {}), ...datos } } },
        })}
        onCambiar={() => { limpiarClasificacion(); if (documentoReabierto) navigate('/similarity-search', { replace: true }); }}
      />
    );
  }

  return (
    <div className={`similarity-search-page${ordersEnabled ? ' entrada-documentos' : ''}`}>
      <h1>{ordersEnabled ? 'Entrada de documentos' : 'Análisis de plano'}</h1>
      {ordersEnabled && (
        <p className="entrada-subtitulo">
          Sube la petición de oferta o el pedido, escaneado o recibido por correo.
          Después la herramienta te pregunta de qué se trata.
        </p>
      )}

      <div className="search-container">
        {/* Archivo */}
        <div
          className={`upload-section${isDragOver ? ' drag-over' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <h3>{ordersEnabled ? 'Documento' : 'Archivo a analizar'}</h3>
          {selectedFile ? (
            <div className="file-info">
              <p>{selectedFile.name}</p>
              {fileUploadedToMCP && mcpFolderPath && (
                <p className="mcp-hint">Guardado en: {mcpFolderPath}</p>
              )}
              <div className="file-actions">
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setFileUploadedToMCP(false);
                    setMcpFolderPath(null);
                    setMcpFileHash(null);
                  }}
                  className="btn-clear"
                >
                  Cambiar archivo
                </button>
                <button
                  onClick={handleCheckDuplicate}
                  disabled={checkingDuplicate || analyzing}
                  className="btn-analyze"
                >
                  {checkingDuplicate ? 'Analizando...' : 'Analizar'}
                </button>
                <button
                  onClick={handleQuickSearch}
                  disabled={checkingDuplicate || analyzing}
                  className="btn-quick-search"
                >
                  {checkingDuplicate ? 'Buscando...' : 'Búsqueda rápida'}
                </button>
              </div>
            </div>
          ) : (
            <div className="file-upload">
              <input
                type="file"
                id="file-input"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {ordersEnabled ? (
                <label htmlFor="file-input" className="zona-arrastre">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 16V4m0 0L7 9m5-5 5 5" />
                    <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
                  </svg>
                  <strong>Arrastra aquí el documento</strong>
                  <span>o pulsa para elegirlo · PDF o imagen</span>
                </label>
              ) : (
                <>
                  <label htmlFor="file-input" className="btn-upload">
                    Seleccionar archivo
                  </label>
                  <p className="drop-hint">o arrastra un archivo aquí</p>
                </>
              )}
            </div>
          )}
        </div>

        {ordersEnabled && !selectedFile && (
          <p className="entrada-sin-documento">
            ¿No hay documento?{' '}
            <button type="button" onClick={() => navigate('/orders/new')}>Crear pedido a mano</button>
            {' · '}
            <button type="button" onClick={() => navigate('/offers/new')}>Crear oferta a mano</button>
            {' '}(clientes que traen la pieza en la mano).
          </p>
        )}

        {/* Indicador de análisis en progreso */}
        {analyzing && (
          <div className="analyzing-indicator">
            <div className="spinner-small"></div>
            <span>{analysisStatusText}</span>
          </div>
        )}

        {SHOW_SIMILARITY_SEARCH && (
          <>
            {/* Filtros */}
            <SearchFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              enabledOperations={enabledOperations}
            />

            {/* Pesos de similitud */}
            <SimilarityWeights
              weights={weights}
              onChange={handleWeightsChange}
            />

            {/* Botón buscar */}
            <button
              onClick={performSearch}
              disabled={!selectedFile || loading}
              className="btn-search"
            >
              {loading ? (
                searchStatus === 'pending' ? 'Iniciando búsqueda...' :
                searchStatus === 'processing' ? 'Analizando pieza...' :
                'Buscando...'
              ) : 'Buscar piezas similares'}
            </button>

            {loading && searchStatus && (
              <div className="status-message">
                {searchStatus === 'pending' && 'Preparando búsqueda...'}
                {searchStatus === 'processing' && 'Analizando la pieza y buscando similitudes...'}
              </div>
            )}
          </>
        )}

        {error && <div className="error-message">{error}</div>}
        {infoMessage && <div className="info-message">{infoMessage}</div>}

        {/* Resultados de duplicados */}
        {duplicateMatches !== null && (
          <div className="duplicate-results-section">
            {duplicateMatches.length > 0 ? (
              <div className="comparison-action">
                <button onClick={() => setShowDuplicateComparison(true)} className="btn-compare">
                  Comparar lado a lado ({duplicateMatches.length} similares)
                </button>
              </div>
            ) : (
              <p className="no-duplicates-message">No se encontraron piezas similares</p>
            )}
            {/* NUBBI-102: entrada a búsqueda por elementos, haya o no similares */}
            {elementsSearchEnabled && elementsQueryPages.length > 0 && (
              <div className="comparison-action">
                <button onClick={() => setShowElementsSearch(true)} className="btn-compare">
                  Búsqueda por elementos
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resultados */}
      {results && (
        <>
          <QueryMetadataHeader metadata={queryMetadata} queryAnalysis={queryAnalysis} filename={selectedFile?.name} />

          <div className="comparison-action">
            <button onClick={() => setShowComparison(true)} className="btn-compare">
              Comparar análisis lado a lado
            </button>
          </div>

          <SimilarResults
            results={results}
            scores={scores}
            breakdowns={breakdowns}
            onPreview={handlePreview}
            queryAnalysis={queryAnalysis}
          />
        </>
      )}

      <FolderSelector
        isOpen={showFolderSelector}
        onSelect={handleFolderSelected}
        onCancel={handleCancelFolderSelector}
        title="Selecciona carpeta de destino"
      />

      {/* NUBBI-102: modal de búsqueda por elementos */}
      {showElementsSearch && (
        <ElementsSearchModal
          queryDrawingPages={elementsQueryPages}
          excludeHash={queryAnalysis?.hash || null}
          onClose={() => setShowElementsSearch(false)}
        />
      )}

      {outlookAccounts.length > 0 && (
        <div className="outlook-accounts">
          <span className="outlook-accounts-label">Outlook:</span>
          {outlookAccounts.map(email => (
            <div key={email} className="outlook-account-item">
              <span className="outlook-account-email">{email}</span>
              <button onClick={() => handleDisconnectOutlook(email)} className="btn-outlook-disconnect">Desconectar</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SimilaritySearchPage;
